import { CssBaseline, ThemeProvider } from "@mui/material";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { isIOS, isMobile, isSafari } from "react-device-detect";
import { FacebookProvider } from "react-facebook";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import { useAnalyticsTracker } from "~/app/hooks/analytics/useAnalyticsTracker";
import { useAuthStore } from "~/app/stores/auth";
import type { TUser } from "~/components";
import { AddToHomeScreen } from "./components/Home/AddToHomeScreen";
import { AddToHomeScreenSafari } from "./components/Home/AddToHomeScreenSafari";
import { FB_APP_ID, GOOGLE_CLIENT_ID, TEMP_SESSIONTOKEN } from "./constants/providers";
import { queryClient } from "./queryClient";
import { Router, router } from "./router/Router";
import { pushNotifications } from "./services/scopely/pushNotifications";
import { appOpen, startupComplete } from "./services/scopely/titanAnalytics";
import { customTheme } from "./theme/theme";

import "./locales/config";
import "./App.scss";
import { useServiceVersion } from "./hooks/useServiceVerion";
import { useCommodityBalance } from "./stores/commodities";

const ModalNotLogged = lazy(() => import("./components/layout/ModalNotLogged/ModalNotLogged"));
const LazyBackground = lazy(() => import("./components/layout/LazyBackground/LazyBackground"));

const TanStackRouterDevtools =
  process.env.NODE_ENV === "production"
    ? () => null
    : lazy(() =>
        import("@tanstack/router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools,
        })),
      );

const ReactQueryDevtools =
  process.env.NODE_ENV === "production"
    ? () => null
    : lazy(() =>
        import("@tanstack/react-query-devtools").then((res) => ({
          default: res.ReactQueryDevtools,
        })),
      );

export function App() {
  const { setToken, token, user, userData, modelOpenNotLogged, setModelOpenNotLogged } =
    useAuthStore();
  const diamondsBalance = useCommodityBalance("Diamonds");
  const trackUI = useAnalyticsTracker("sys.ui");

  useServiceVersion(); // 👈 verifica se há atualização

  const isRunningAsPWA = () => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: old code, need tests before refactoring
  useEffect(() => {
    if (!token) {
      setToken(TEMP_SESSIONTOKEN);
    }
    if (userData) {
      appOpen(diamondsBalance, userData as TUser);

      if (isRunningAsPWA()) {
        trackUI({
          screen_id: "pwa",
          screen_name: "web_portal_pwa",
          screen_type: "screen",
          screen_category: "web_portal_pwa",
          screen_subcategory: "web_portal_pwa",
          screen_content: "app_load",
          screen_section: null,
          interaction_type: "load",
          interaction_object: null,
          interaction_object_category: "display",
          flow_name: "web_portal",
          flow_order: 1,
        });

        // Handle first time opened logic
        const pwaFirstOpenDone = localStorage.getItem("pwa_first_open_done");
        if (!pwaFirstOpenDone) {
          // console.log("First time opening the PWA!");
          // TODO: Add your "first time only" logic here
          localStorage.setItem("pwa_first_open_done", "true");
        }

        // Initialize and subscribe/sync push notifications
        if (user?.Id) {
          // disable push notifications for now as they are not working correctly and we want to avoid spamming users with permission requests until we have it working well
          // pushNotifications.subscribe(String(user.Id));
        }
      } else if (user?.Id) {
        // Even if not in PWA mode, we might want to sync if they already accepted
        // disable push notifications for now as they are not working correctly and we want to avoid spamming users with permission requests until we have it working well
        // pushNotifications.sync(String(user.Id));
      }
    }
    startupComplete();
  }, [token, setToken, userData, user]);

  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <FacebookProvider appId={FB_APP_ID}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={customTheme({ darkMode: false })}>
              <CssBaseline />
              <Toolbar sx={{ display: { xs: "flex", lg: "none" } }} />

              {!isRunningAsPWA() &&
                (isMobile && isIOS && isSafari ? <AddToHomeScreenSafari /> : <AddToHomeScreen />)}

              <Box
                display="flex"
                justifyContent="center"
                sx={{
                  minHeight: "100dvh",
                  backgroundColor: "#382053",
                }}
              >
                <Suspense fallback={null}>
                  <LazyBackground />
                </Suspense>

                <Router />

                <Suspense fallback={null}>
                  <ModalNotLogged
                    open={modelOpenNotLogged}
                    handleClose={() => setModelOpenNotLogged(false)}
                  />
                </Suspense>
              </Box>
            </ThemeProvider>

            <ToastContainer />

            <Suspense fallback={null}>
              <TanStackRouterDevtools router={router} position="bottom-right" />
              <ReactQueryDevtools />
            </Suspense>
          </QueryClientProvider>
        </FacebookProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}
